import React, { useEffect, useState } from 'react';
import {
  Container,
  Grid,
  Paper,
  Toolbar,
  Stack,
  IconButton,
  Typography,
  Box,
  Divider,
  Button,
  CircularProgress,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate, useLocation, useParams } from 'react-router';

import ErrorSnackBar from '@/components/shared/ErrorSnackBar';
import LoadingMask from '@/components/shared/LoadingMask';
import AuthorizerFields from './AuthorizerFields';
import { AUTHORZIER_TYPES } from './AuthorizerFields/utils';
import cleanEmptyValues from '@/util/cleanEmptyValues';
import { getProjectDetailsPath } from '@/util/routePaths';
import {
  useProjectAuthorizers,
  useProjectAuthorizersActions,
} from '@/providers';
import ProjectAuthorizerNameField from './ProjectAuthorizerNameField';

function buildInitialAuthorizerPayload(authorizer) {
  if (!authorizer?.type || authorizer.type === AUTHORZIER_TYPES.NONE) {
    return null;
  }
  if (authorizer.type === AUTHORZIER_TYPES.API_KEY) {
    return (
      cleanEmptyValues({
        type: authorizer.type,
        keys: Array.isArray(authorizer.keys) ? authorizer.keys : [],
      }) || null
    );
  }
  if (authorizer.type === AUTHORZIER_TYPES.OIDC) {
    return (
      cleanEmptyValues({
        type: authorizer.type,
        oidc: authorizer.oidc || {},
      }) || null
    );
  }
  return null;
}

export default function ProjectAuthorizerForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { error } = useProjectAuthorizers();
  const { loadAuthorizer, createAuthorizer, updateAuthorizer, clearError } =
    useProjectAuthorizersActions();

  const authorizer_id = new URLSearchParams(location.search).get('authorizer');
  const is_edit = Boolean(authorizer_id);

  const [ready, setReady] = useState(!is_edit);
  const [initial_authorizer, setInitialAuthorizer] = useState(null);
  const [name, setName] = useState('');
  const [name_touched, setNameTouched] = useState(false);
  const [authorizer_payload, setAuthorizerPayload] = useState(undefined);
  const [authorizer_error, setAuthorizerError] = useState(false);
  const [config_ready, setConfigReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!is_edit) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const authorizer = await loadAuthorizer(authorizer_id);
      if (cancelled) {
        return;
      }
      if (authorizer) {
        setInitialAuthorizer(authorizer);
        setName(authorizer.name || '');
      }
      setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [is_edit, authorizer_id, loadAuthorizer]);

  const initial_name = (initial_authorizer?.name || '').trim();
  const initial_payload_json = JSON.stringify(
    buildInitialAuthorizerPayload(initial_authorizer),
  );
  const name_changed = name.trim() !== initial_name;
  const config_changed =
    config_ready &&
    JSON.stringify(authorizer_payload ?? null) !== initial_payload_json;
  const dirty = name_changed || config_changed;
  const name_error = name_touched && !name.trim();
  const api_keys = authorizer_payload?.keys || [];
  const type_selected =
    authorizer_payload?.type === AUTHORZIER_TYPES.API_KEY ||
    authorizer_payload?.type === AUTHORZIER_TYPES.OIDC;
  const api_key_invalid =
    authorizer_payload?.type === AUTHORZIER_TYPES.API_KEY &&
    (api_keys.length === 0 ||
      api_keys.some((key_row) => !key_row?.client_id || !key_row?.key));

  const handleAuthorizerChange = (next_payload, next_error) => {
    setAuthorizerPayload(next_payload);
    setAuthorizerError(Boolean(next_error));
    setConfigReady(true);
  };

  const handleSubmit = async () => {
    const name_value = name.trim();
    if (!name_value || !type_selected || authorizer_error || api_key_invalid) {
      setNameTouched(true);
      return;
    }
    setSubmitting(true);
    const payload = {
      name: name_value,
      ...authorizer_payload,
    };
    try {
      if (is_edit) {
        await updateAuthorizer(authorizer_id, payload);
      } else {
        await createAuthorizer(payload);
      }
      navigate(getProjectDetailsPath(id));
    } catch {
      setSubmitting(false);
    }
  };

  if (!ready) {
    return (
      <Container>
        <LoadingMask />
      </Container>
    );
  }

  const show_fields = !is_edit || Boolean(initial_authorizer);

  return (
    <Container>
      <Paper sx={{ display: 'flex', width: '100%' }}>
        <Grid container spacing={2} sx={{ flexGrow: 1 }}>
          <Grid size={{ xs: 12, md: 12, lg: 12 }}>
            <Paper sx={{ display: 'flex', width: '100%' }} elevation={2}>
              <Toolbar>
                <Stack spacing={2} direction='row' alignItems='center'>
                  <IconButton
                    onClick={() => navigate(getProjectDetailsPath(id))}
                    disabled={submitting}
                  >
                    <ArrowBack />
                  </IconButton>
                  <Typography variant='h5'>
                    {is_edit
                      ? `Edit ${initial_authorizer?.name || 'Project Authorizer'}`
                      : 'Add Project Authorizer'}
                  </Typography>
                </Stack>
              </Toolbar>
            </Paper>
          </Grid>

          {show_fields ? (
            <Grid size={{ xs: 12, md: 12, lg: 12 }}>
              <Box
                padding='15px'
                sx={{
                  maxWidth: 800,
                }}
              >
                <Grid container spacing={2} sx={{ flexGrow: 1 }}>
                  <Grid size={12}>
                    <ProjectAuthorizerNameField
                      value={name}
                      error={name_error}
                      disabled={submitting}
                      onChange={(event) => {
                        setName(event.target.value);
                        setNameTouched(true);
                      }}
                    />
                  </Grid>
                  <Grid size={12}>
                    <AuthorizerFields
                      initialValues={initial_authorizer || undefined}
                      onChange={handleAuthorizerChange}
                      stacked
                      allowNone={false}
                      typeVariant='radio'
                      lockType={is_edit}
                      showHeading={false}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          ) : (
            <Grid size={12}>
              <Box padding='15px'>
                <Typography color='error'>
                  This authorizer could not be loaded.
                </Typography>
              </Box>
            </Grid>
          )}

          <Grid size={{ xs: 12, md: 12, lg: 12 }}>
            <Divider />
            <Toolbar>
              <Stack spacing={2} direction='row'>
                <Button
                  variant='contained'
                  color='brand'
                  onClick={handleSubmit}
                  disabled={
                    !show_fields ||
                    submitting ||
                    !name.trim() ||
                    !type_selected ||
                    authorizer_error ||
                    api_key_invalid ||
                    !dirty
                  }
                  startIcon={
                    submitting ? <CircularProgress size={20} /> : undefined
                  }
                >
                  Submit
                </Button>
                <Button
                  variant='outlined'
                  onClick={() => navigate(getProjectDetailsPath(id))}
                  disabled={submitting}
                >
                  Cancel
                </Button>
              </Stack>
            </Toolbar>
          </Grid>
        </Grid>
      </Paper>
      <ErrorSnackBar message={error} onClose={clearError} />
    </Container>
  );
}
